import { QueryClient, useQuery } from '@tanstack/react-query';
import type {
  AccountDto,
  ActivityDto,
  CardMappingDto,
  DeviceDto,
  HomeDto,
  InboxDto,
  LoanDto,
  MeDto,
  PayeeDto,
  PayeeRuleDto,
  PlanDto,
  PredictionModelDto,
  ReflectDto,
  ScheduledDto,
  TransactionDto,
  UnmappedCardDto,
} from '@koverta/shared';
import { get, onSuccessfulWrite } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (n, e) =>
        n < 2 && !(e instanceof Error && 'status' in e && (e as { status: number }).status < 500),
      refetchOnWindowFocus: true,
    },
  },
});

export const keys = {
  me: ['me'] as const,
  plan: (m: string) => ['plan', m] as const,
  accounts: ['accounts'] as const,
  transactions: (f: Record<string, string | undefined>) => ['transactions', f] as const,
  inbox: ['inbox'] as const,
  dismissed: ['inbox', 'dismissed'] as const,
  home: ['home'] as const,
  reflect: (q: Record<string, string | undefined>) => ['reflect', q] as const,
  payees: ['payees'] as const,
  rules: ['rules'] as const,
  cards: ['cards'] as const,
  devices: ['devices'] as const,
  rates: ['rates'] as const,
  activity: ['activity'] as const,
  scheduled: (accountId?: string) => ['scheduled', accountId ?? 'all'] as const,
  loans: ['loans'] as const,
  ynabStatus: ['ynab-status'] as const,
  predictions: ['predictions'] as const,
};

const qs = (f: Record<string, string | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const useMe = () =>
  useQuery({ queryKey: keys.me, queryFn: () => get<MeDto>('/api/me'), staleTime: 60_000 });
export const usePlan = (month: string) =>
  useQuery({ queryKey: keys.plan(month), queryFn: () => get<PlanDto>(`/api/plan/${month}`) });
export const useAccounts = () =>
  useQuery({ queryKey: keys.accounts, queryFn: () => get<AccountDto[]>('/api/accounts') });
export const useTransactions = (f: Record<string, string | undefined>, enabled = true) =>
  useQuery({
    queryKey: keys.transactions(f),
    queryFn: () => get<TransactionDto[]>(`/api/transactions${qs(f)}`),
    enabled,
  });
export const useInbox = () => useQuery({ queryKey: keys.inbox, queryFn: () => get<InboxDto>('/api/inbox') });
export const useDismissed = (enabled: boolean) =>
  useQuery({ queryKey: keys.dismissed, queryFn: () => get<InboxDto>('/api/inbox/dismissed'), enabled });
export const useHome = () => useQuery({ queryKey: keys.home, queryFn: () => get<HomeDto>('/api/home') });
export const useReflect = (q: Record<string, string | undefined>) =>
  useQuery({ queryKey: keys.reflect(q), queryFn: () => get<ReflectDto>(`/api/reflect${qs(q)}`) });
export const usePayees = () =>
  useQuery({ queryKey: keys.payees, queryFn: () => get<PayeeDto[]>('/api/payees') });
export const useRules = () =>
  useQuery({ queryKey: keys.rules, queryFn: () => get<PayeeRuleDto[]>('/api/payee-rules') });
export const useCards = () =>
  useQuery({
    queryKey: keys.cards,
    queryFn: () => get<{ cards: CardMappingDto[]; unmapped: UnmappedCardDto[] }>('/api/cards'),
  });
export const useDevices = () =>
  useQuery({ queryKey: keys.devices, queryFn: () => get<DeviceDto[]>('/api/devices') });
export interface RatesDto {
  current: { date: string; rate: number; source: 'nbs' | 'manual' | 'fallback' };
  nbsToday: { date: string; rate: number } | null;
  history: { date: string; rate: number; source: 'nbs' | 'manual'; nbs: number | null; note: string }[];
}
export const useRates = () =>
  useQuery({ queryKey: keys.rates, queryFn: () => get<RatesDto>('/api/rates'), staleTime: 300_000 });
export const useActivity = () =>
  useQuery({ queryKey: keys.activity, queryFn: () => get<ActivityDto[]>('/api/activity') });

export const useScheduled = (accountId?: string) =>
  useQuery({
    queryKey: keys.scheduled(accountId),
    queryFn: () => get<ScheduledDto[]>(`/api/scheduled${qs({ accountId })}`),
  });
export const usePredictionModel = () =>
  useQuery({
    queryKey: keys.predictions,
    queryFn: () => get<PredictionModelDto>('/api/predictions/model'),
  });
export const useLoans = () => useQuery({ queryKey: keys.loans, queryFn: () => get<LoanDto[]>('/api/loans') });
export interface YnabStatusDto {
  /** The budget has data that an import would delete; the user must confirm. */
  hasData: boolean;
  reason: string | null;
  existing: { transactions: number; schedules: number; accounts: number };
  removes: { accounts: number; categories: number };
  lastImport: { batchId: string; name: string; at: string; canReset: boolean } | null;
}
export const useYnabStatus = () =>
  useQuery({ queryKey: keys.ynabStatus, queryFn: () => get<YnabStatusDto>('/api/import/ynab') });

/** Which cached queries a server change scope makes stale (see server lib/events.ts). */
const SCOPE_KEYS: Record<string, string[]> = {
  plan: ['plan', 'home', 'predictions'],
  transactions: [
    'transactions',
    'home',
    'payees',
    'plan',
    'scheduled',
    'loans',
    'ynab-status',
    'predictions',
  ],
  accounts: ['accounts', 'home', 'loans', 'scheduled', 'predictions'],
  inbox: ['inbox', 'home'],
  settings: ['me', 'cards', 'devices', 'rules', 'payees'],
  activity: ['activity', 'home'],
  rates: ['rates', 'plan', 'accounts', 'home', 'predictions'],
  predictions: ['predictions'],
};

export function invalidateScopes(scopes: string[]) {
  const roots = new Set(scopes.flatMap((s) => SCOPE_KEYS[s] ?? []));
  for (const root of roots) void queryClient.invalidateQueries({ queryKey: [root] });
}

// Every successful write refreshes whatever is on screen and marks the rest stale (refetched when next opened).
onSuccessfulWrite(() => void queryClient.invalidateQueries());
